import React from 'react'
import cn from 'classnames'
import { FormDamage } from 'pages/damage_reports/DamageReportForm/types'
import classes from './VehiclePicker.module.scss'

interface VehiclePickerProps {
  damages: FormDamage
  mirror?: boolean
  setDamage: (damage: string, value: any) => void
}

export const VehiclePicker: React.FunctionComponent<VehiclePickerProps> = ({
  damages,
  mirror = false,
  setDamage,
}) => {
  const side = mirror ? 'Right' : 'Left'

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 671.68 271.97"
      width="671.68"
      height="271.97"
      className={cn(classes.illustration, { [classes.mirror]: mirror })}
    >
      <path
        className={classes.medium}
        d="M625.75 9.5V5.96l-13.39-3.97H586.8m52.5 205.08l-.31-11 1.18-4.12m-509.63-7.12a78 78 0 019.63 1.36 23.6 23.6 0 016.27 2.19c9.06 4.77 15.87 12 20.91 21.21 3.81 9.29 5.18 19.68 5 30.78h103.14m-97.08-26.31h-9.42m106.5 0h-16.25"
      />
      <path className={classes.line} d="M275.81 45.04l-6.13.19" />
      <path
        className={classes.medium}
        d="M198.06 56.73l-5.87-1.16m83.61-10.7l-5.62.25"
      />
      <path
        className={classes.line}
        d="M142.27 123.68c3.75-4.7 7.9-9.67 12.31-14.82"
      />
      <path
        className={classes.medium}
        d="M275.6 158.9l-9.3-.04M183.9 50.75c9-9.56 22.34-20.07 37.18-30.93A129.18 129.18 0 01275.8 2c-25 4.77-51.59 20.7-80 49.74"
      />
      <path
        className={classes.line}
        d="M638.99 196.07h-3.9M78.94 225.28l10.92-1.23m74.6.62h6.94"
      />
      <path
        className={classes.medium}
        d="M557.68 221.66h-9.24m-72.35 2.2h-8.04"
      />
      <g
        id="tireRearLeft"
        className={cn({
          [classes.selected]:
            damages.tires[`rear${side}` as keyof FormDamage['tires']],
        })}
        onClick={() =>
          setDamage(
            `tires.rear${side}`,
            !damages.tires[`rear${side}` as keyof FormDamage['tires']]
          )
        }
      >
        <circle className={classes.outline} cx="512.61" cy="232.89" r="39.07" />
        <circle
          className={classes.outerTire}
          cx="512.57"
          cy="232.89"
          r="37.57"
        />
        <circle className={classes.line} cx="512.57" cy="233.35" r="22.74" />
        <circle className={classes.line} cx="512.61" cy="233.35" r="19.57" />
        <path
          className={classes.thin}
          d="M512.57 229.27a3.88 3.88 0 11-3.87 3.88 3.88 3.88 0 013.87-3.88zm0 3.88zm3.24 2.14l-3.24-2.14-3.16 2.24"
        />
      </g>
      <g
        id="tireFrontLeft"
        className={cn({
          [classes.selected]:
            damages.tires[`front${side}` as keyof FormDamage['tires']],
        })}
        onClick={() =>
          setDamage(
            `tires.front${side}`,
            !damages.tires[`front${side}` as keyof FormDamage['tires']]
          )
        }
      >
        <circle className={classes.outline} cx="127.12" cy="228.9" r="39.07" />
        <circle
          className={classes.outerTire}
          cx="127.12"
          cy="228.9"
          r="37.57"
        />
        <circle className={classes.line} cx="127.13" cy="229.15" r="22.74" />
        <circle className={classes.line} cx="127.13" cy="229.15" r="19.57" />
        <path
          className={classes.thin}
          d="M127.13 225.27a3.88 3.88 0 11-3.88 3.88 3.88 3.88 0 013.88-3.88zm0 4zm3.2 2l-3.24-2.14-3.17 2.24"
        />
      </g>
      <g
        id="engineHood"
        className={cn({ [classes.selected]: damages.engineHood })}
        onClick={() => setDamage(`engineHood`, !damages.engineHood)}
      >
        <path
          className={classes.outline}
          d="M125.66 123.26a.65.65 0 000-.2 1.34 1.34 0 00-.07-.18l-.1-.15s-.09-.1-.15-.15l-.06-.06h-.09l-.18-.07a.71.71 0 00-.2 0H116l-1.36-3.82a.78.78 0 00-.1-.15.62.62 0 00-.11-.17.88.88 0 00-.15-.12 1.4 1.4 0 00-.16-.1.65.65 0 00-.2 0 .47.47 0 00-.17 0 .69.69 0 00-.2 0 1.1 1.1 0 00-.18 0c-5.78 2.27-11.06 4.17-16.06 5.8a384.69 384.69 0 00-45.24 13.51c-15.3 5.75-23.78 11.66-26.69 18.6a1 1 0 00.1 1 1 1 0 00.85.43c3.51-.09 5.87.78 7.42 2.75.13.16.25.34.37.52a1 1 0 00.84.45 1 1 0 00.34-.06c2-.71 4.23-1.41 6.78-2.08 7.54-2 17.72-3.8 32-5.72a.76.76 0 00.16-.06.5.5 0 00.13 0c5.06-2.33 9.64-4.23 14.35-6l26.14-7.86a1 1 0 00.55-.4l10.06-15v-.08a1.07 1.07 0 00.07-.19.58.58 0 000-.19.21.21 0 000-.18z"
        />
        <path
          className={classes.medium}
          d="M124.68 123.35h-9.37l-1.6-4.49M74 152.26c4.35-2 9-4 14.48-6l26.15-7.85 10.06-15M41.82 158c8.63-2.26 19.87-4.09 32.17-5.74m-39.45 7.26c.14.19.28.38.42.59 2-.74 4.34-1.44 6.86-2.11m71.89-39.14c-5.42 2.14-10.83 4.14-16.21 5.84a382 382 0 00-45.08 13.63c-13 4.89-23 10.57-26.11 18.06 3.38-.09 6.28.66 8.23 3.13"
        />
      </g>
      <g
        id="tailgate"
        className={cn({ [classes.selected]: damages.tailgate })}
        onClick={() => setDamage(`tailgate`, !damages.tailgate)}
      >
        <path
          className={classes.outline}
          d="M644.05 160.73a1.08 1.08 0 00.21-.62v-2.3a1 1 0 00-1-1h-.92v-6.66l-.14-18.39a1 1 0 00-.3-.7l-1.5-1.5-6.25-80.21 1.05-.57a1 1 0 00.51-1.06L632 26.86l1.11-.81a1 1 0 00.33-1.2L626.67 9.1v-.06a.71.71 0 00-.12-.18l-.11-.12-.18-.11a.51.51 0 00-.15-.07h-.47l-5.49.88H620a.59.59 0 00-.14.06 1.51 1.51 0 00-.18.09l-.14.13a1.49 1.49 0 00-.12.14.58.58 0 00-.08.18.87.87 0 00-.07.17 1.34 1.34 0 000 .2.76.76 0 000 .15v.05c4.53 17.51 8 42.44 10.15 74.11l.8 10.44c2.38 36 3.56 65.82 3.85 96.79a1 1 0 001 1h8.17a1 1 0 00.78-.38 1 1 0 00.19-.86l-1.33-5.44a1 1 0 00-.41-.6l-.86-.57.85-22.36z"
        />
        <path
          className={classes.hiddenHover}
          d="M636.23 1l-12.17 11.45 9.04 65.77 3.94 90.99 2.26 20.12 16.62 10.18 15.76-101.94-27.42-91.83L636.23 1z"
        />
        <path
          className={classes.fillDark}
          d="M633.54 49.68L630 50.76a.43.43 0 01-.54-.29.43.43 0 01.17-.47l3.08-2.12a1 1 0 111.15 1.67 1.17 1.17 0 01-.32.13zM631 26.91a1.94 1.94 0 01-1.29.38l-1.35.12c-.89.05-1.79.1-2.71.07a.14.14 0 01-.14-.15.15.15 0 01.09-.14c.85-.33 1.72-.59 2.58-.85l1.3-.34a2 2 0 011.35-.08.61.61 0 01.31.79.48.48 0 01-.14.2z"
        />
        <path
          className={classes.medium}
          d="M625.75 9.5l6.77 15.74-1.62 1.18 3.82 21.48-1.62.88 6.33 81.22 1.77 1.77.14 18.39v7.65h1.92v2.3l-1.77 2.27-.88 23.24 1.32.89 1.33 5.44h-3.09m0 0h-5.08c-.33-35.72-1.88-67-3.85-96.84l-.8-10.46c-2.13-30.51-5.4-55.81-10.18-74.28m0 0l5.49-.87"
        />
      </g>
      <g
        id="frontDoorLeft"
        className={cn({
          [classes.selected]: damages[`frontDoor${side}` as keyof FormDamage],
        })}
        onClick={() =>
          setDamage(
            `frontDoor${side}`,
            !damages[`frontDoor${side}` as keyof FormDamage]
          )
        }
      >
        <path
          className={classes.outline}
          d="M271.18 45.09a.14.14 0 000-.07.94.94 0 00-.07-.3v-.06a.92.92 0 00-.19-.25 1 1 0 00-.63-.27h-.07c-33.79 1.51-62.58 3.44-72.72 11.84-3.36 3.24-6.93 6.58-10.39 9.81-16.92 15.8-32.9 30.73-33.32 42.49a106.78 106.78 0 00-1.79 13.89 128 128 0 00.17 14v40.12a1 1 0 00.32.73 120.46 120.46 0 0111.62 12.3 76.12 76.12 0 019.19 14.09 59.88 59.88 0 015.28 15.89 77.23 77.23 0 011.05 16.79 1 1 0 001 1h73.7a.62.62 0 00.32-.09 1.79 1.79 0 00.8-.75 5.57 5.57 0 00.31-.58 10.13 10.13 0 00.42-1.06c.24-.69.43-1.37.62-2.06.36-1.37.67-2.74 1-4.1.58-2.74 1.07-5.49 1.52-8.24.33-2 .63-4 .91-6a1 1 0 000-.17c.47-3.44.9-6.88 1.24-10.34.27-2.77.49-5.56.61-8.36.06-1.39.08-2.84.07-4.19l.14-4.16.55-16.46 4.39-11.3a1.13 1.13 0 00.06-.32l1.26-28.43c.2-4.74.46-9.48.6-14.22l.4-14.22 1.63-56.89a.43.43 0 010-.06z"
        />
        <path
          className={classes.line}
          d="M204.42 61.9c6-3 22.86-5.38 43.33-7.54a48.06 48.06 0 019.37.27 2.86 2.86 0 012.43 2.67c.5 8.57-1 23.1-6.87 50.25a7.3 7.3 0 01-2.19 4.1 21.23 21.23 0 01-5.77 3.52l-41.58 11.19a6.56 6.56 0 01-1.65.22h-4.18c2.47-22.44 5.03-44.08 7.11-64.68z"
        />
        <path
          className={classes.medium}
          d="M259.24 214.06h-80.83m91.77-168.94c-31.81 1.42-62.11 3.31-72.12 11.61m0 0c-19 18.33-43 37.9-43.41 51.7M153.19 161v15.33c23.38 21.68 28.19 39.74 27.46 59.84h73.62c2.2-1 7.12-32.08 6.88-44.83l.71-21 4.44-11.46 1.79-40.46 2.09-73.28M153.19 161v-24.9m1.46-27.67a104.33 104.33 0 00-1.46 27.67"
        />
        <path
          className={classes.line}
          d="M199.76 67.86c-1.87 16.71-4.26 36.83-7.09 59.7-6.48 1-12.28 2.55-19 2.37a2.81 2.81 0 01-2.75-2.83l.34-34.27 24.44-25z"
        />
        <path
          className={classes.line}
          d="M154.58 108.86l2.33-2.71 13-14.64c10.61-11.1 20.9-21.58 29.84-29.43a18.64 18.64 0 017.18-3.9c16.84-4.84 34.28-6.17 51.95-6.08a7.49 7.49 0 013.94 5.49c1.55.24-6.82 51.6-8.44 53.35a27.75 27.75 0 01-12.81 7.6 361.44 361.44 0 01-46.76 11.83c-10 1.55-19.33 2.83-27.71 3.66-4.51.8-9.14 1.49-13.86 2.07m0 24.88l5.37-.65a7.43 7.43 0 003.36-1.25 53.8 53.8 0 0012.59-12.2l.4-1a6.38 6.38 0 000-4.82l-3-7.57"
        />
      </g>
      <g
        id="bumperFront"
        className={cn({ [classes.selected]: damages.bumperFront })}
        onClick={() => setDamage(`bumperFront`, !damages.bumperFront)}
      >
        <path
          className={classes.outline}
          d="M99.06 190.77l-11.78-.7c-2.61-.15-7.15-1.48-9-3.61a114.82 114.82 0 01-12.61-14.88v-.05l-.08-.07h-.36l-22.33 3.68a5 5 0 01-3.77-.91l-1.5-1.11h-.28a.27.27 0 00-.09 0h-.24l-.09.08a.36.36 0 000 .09.2.2 0 000 .08c-4.45 16.07-9.15 24.31-14.35 25.16l-1.73.92.7 2a1 1 0 00.61.61l2.49.86c2.13 3.25 2.66 6.25 1.65 9.39l-4.92 2.87a3.49 3.49 0 00-1.72 3v6.89a9.47 9.47 0 007 9.13l26.24 7.15h26.16a1 1 0 001-1v-15c3.77-15.69 10.71-26.87 20.61-33.24-.12.05-1.47-1.44-1.61-1.34z"
        />
        <path
          className={classes.medium}
          d="M21.7 199.13l.7 2 2.8 1c2.21 3.23 3.31 6.75 1.84 10.89L21.8 216a2.48 2.48 0 00-1.24 2.15v6.89a8.46 8.46 0 006.24 8.16l26.2 7.17h25.9v-15.09c3.89-16.26 11-27.47 21.07-34"
        />
        <path
          className={classes.line}
          d="M65.31 171.88L43 175.63a5.46 5.46 0 01-4.16-1l-1.51-1.12M21.7 199.13c5.25-.1 10.47-7 15.62-25.62m28-1.63A114.2 114.2 0 0078 186.82c1.83 2.15 6.46 3.58 9.28 3.75l12.76.75"
        />
      </g>
      <g
        id="sidePanelOrDoor"
        className={cn({
          [classes.selected]:
            damages[`sidePanelOrDoor${side}` as keyof FormDamage],
        })}
        onClick={() =>
          setDamage(
            `sidePanelOrDoor${side}`,
            !damages[`sidePanelOrDoor${side}` as keyof FormDamage]
          )
        }
      >
        <path
          className={classes.outline}
          d="M614.05 191c.94-16.15 1.38-35.32 1.32-58.55-1.89-46.6-4.7-90.45-9.09-129.45-4.34-.61-9.23-1.06-14.24-1.52L586.9 1h-32.59l-61.92-1h-158l-58.6 1a1 1 0 00-1 1l-.27 211.92a.38.38 0 000 .34v25.9a.42.42 0 000 .12.53.53 0 000 .12 1 1 0 001 1h189.4a1 1 0 001-.84c1-5.84 2-11.79 4-20.36a40.27 40.27 0 011.8-5.7 35.81 35.81 0 014.62-8.2 43.91 43.91 0 0119.67-15c11.72-4.38 24.2-3.89 37 1.42 7.74 4 14.11 10.1 19.51 18.69 3.42 4.8 5.48 13.67 6.14 26.38a1.13 1.13 0 000 .18l1.32 5.59a1 1 0 001 .77h.17l47.85-7.96h.09a.7.7 0 00.2-.08.42.42 0 00.14-.09.93.93 0 00.15-.13l.1-.14a.75.75 0 00.08-.18 1.09 1.09 0 000-.17v-.07l2-31.93.37-11.05z"
        />
        <path
          className={classes.medium}
          d="M575.56 50.45c-100.08-.16-194.08-.07-276.93.49l-5.62.12a6.41 6.41 0 00-5.69 6c-.6 1.39 2.22 45.08 5.46 63.65 1 11.63 14.21 11.77 19.66 11.65l100.52-1 190.43-.5 1.61-.08a3.06 3.06 0 002.88-3V120c-1.46-22-3.17-42.79-6.14-57.56a12.9 12.9 0 00-1.9-4.67c-.48-.72-1-1.41-1.52-2.07a10.56 10.56 0 00-5.23-3.52c-4.62-1.31-10.92-1.62-17.53-1.73z"
        />
        <path
          className={classes.line}
          d="M617.77 6.8v4.92c2.27 4.85 4.23 17.89 8 50.63 2.38 20.78 4 44.63 5.14 70.38m2.6 2.09c-.76-1.9-3.66-2.81-8.41-2.88h-10.23c.06 21.71-.3 42.1-1.38 60.11m1.38-60.11c-.57-29-2.2-57.77-4.58-86.35q-1.74-20.85-4-41.57m4 41.57l-328.69-.72-5.79.17m197.03 164.79h-8.49c6-11.93 12.71-22.45 22-28.17a28 28 0 0114.73-4h25.71a20.52 20.52 0 0110.84 3.13c9.17 5.69 17.44 13.64 22.79 29.05h-7.71m58.35.96c-26 1.1-50.51 2.2-55.79 3.4m4.73 24.89q17.6-4.5 49.3-9m2.5-27.49l.37-10.55"
        />
        <path
          className={classes.medium}
          d="M275.49 240.13L275.8 2l58.61-1h158l61.9 1h32.52m22 233.4L561 243.3l-1.33-5.59c-.68-13-2.8-22-6.33-26.91-5.69-9.06-12.39-15.13-19.86-19-12.89-5.35-25.5-6.07-37.82-1.47a44.69 44.69 0 00-20.11 15.3 38.27 38.27 0 00-6.55 14.31c-2.07 8.73-3.08 14.68-4 20.43H275.49m344.77-230c-.23-.85-.45-1.7-.68-2.53-5-3.7-19.61-4.56-32.77-5.85m48.28 200.61V192l-101 .2m-63.33 21.91H275.49m160 26.31V1M611.8 202.6h23.88m-26.85 32.79l3-32.79"
        />
      </g>
      <g
        id="bumperBack"
        className={cn({ [classes.selected]: damages.bumperBack })}
        onClick={() => setDamage(`bumperBack`, !damages.bumperBack)}
      >
        <path
          className={classes.outline}
          d="M643.69 220.62l-4.76-2.27 1.15-.67a1 1 0 00.5-.89l-.28-9.74a.66.66 0 000-.15 1.55 1.55 0 00-.06-.23.43.43 0 000-.07 1.18 1.18 0 00-.19-.25 1 1 0 00-.27-.17h-.18a1.06 1.06 0 00-.19 0h-2.62v-3.58a1 1 0 00-1-1H611.8a1 1 0 00-1 .91l-3 32.79v.16a.42.42 0 000 .12 1.21 1.21 0 000 .18.91.91 0 00.09.17l.11.14a.88.88 0 00.15.12.46.46 0 00.15.09l.2.07h.29l23.4-1.84h.19c7.39-2.08 11.35-6.43 11.77-12.91a1 1 0 00-.46-.98z"
        />
        <path
          className={classes.hiddenHover}
          d="M651.14 208.48h-25.1l-12.55 20.79-1.69 14.03 18.41 11.48 20.93-11.48 4.78-21.64-4.78-13.18z"
        />
        <path
          className={classes.medium}
          d="M639.3 207.07l.28 9.75-2.8 1.62 6.48 3.09c-.39 5.95-4 10-11 12l-23.39 1.85m30.47-28.32h-3.62v-4.46H611.8l-3 32.79"
        />
        <path className={classes.line} d="M635.68 207.07l1.1 11.37" />
      </g>
      <g
        id="Window"
        className={cn({ [classes.selected]: damages.hadWindow })}
        onClick={() => setDamage(`hadWindow`, !damages.hadWindow)}
      >
        <path
          className={classes.outline}
          d="M196.81 51.73a.75.75 0 000-.21v-.17l-.09-.16a1.09 1.09 0 00-.13-.17l-.12-.07-.17-.11h-.21a.5.5 0 00-.13 0l-11.4-.84a1 1 0 00-1.31 0c-20.12 17-45.66 42.82-70.25 68.17 0 0 0 .08-.08.12a1.13 1.13 0 00-.12.18 1.16 1.16 0 000 .18.72.72 0 000 .19 1.52 1.52 0 000 .21v.15l1.61 4.49a.26.26 0 00.05.09 1.1 1.1 0 00.14.21l.09.08a.9.9 0 00.2.14h.1a1 1 0 00.35.07H126a1 1 0 00.71-.29l2.88-2.87c24.31-24.23 46.84-47.37 67-68.79a1.05 1.05 0 00.07-.11 1.22 1.22 0 00.12-.19 1.06 1.06 0 000-.19.57.57 0 000-.14s.03.05.03.03z"
        />
        <path
          className={classes.hiddenHover}
          d="M178.41 47.9l-21.08-8.84-42.71 33.87-10.23 40.63 15.6 6.92 67.08-64.91-8.66-7.67z"
        />
        <path
          d="M195.82 51.71L183.64 51m.26-.22c-19.56 16.55-44.35 41.48-70.19 68.11m11 4.49H126l2.89-2.87m0 0c23.67-23.64 46.11-46.61 66.93-68.8m-82.11 67.15l1.6 4.49m9.37 0h-9.37"
          fill="#333"
          strokeWidth="2"
          stroke="#333"
        />
      </g>
      <g
        id="engineCooler"
        className={cn({ [classes.selected]: damages.engineCooler })}
        onClick={() => setDamage(`engineCooler`, !damages.engineCooler)}
      >
        <path
          className={classes.outline}
          d="M22.61 199.53c5.65-.9 10.15-9.53 14.71-26 1.47-4.89 4.9-12.59 4.25-16.48 0 0-18.79 6.18-18.83 6.36-.18 1-.85 4.17-1 5.13l-1.46.62-1.63 6c-.25.1 1.82 3.78 1.86 3.94l-.51 6.16c-.13.59 1.58 1 1.89 1.23l.21 1.51 4.68.52-.58 2.23-5.64 4.48c-1 .65.12 3.43.2 4.22"
        />
        <path
          className={classes.hiddenHover}
          d="M38.18 161.75l-8.06-3.12-24.72-6.38L0 174.47l2.08 16.26 8.97 8.4 7.56 1.35 6.86-4.03 12.71-34.7z"
        />
        <path
          className={classes.line}
          d="M20.74 179.08l-2.13-3.94 1.63-6 1.46-.62m-3.09 6.62l2.42.27m10.9-4.41L21 176.6l9 1.4m3.32-14.53l-11.26 3.41 11 .1m-10.54 19.51L21 187.62l5.75.91m7.76-29l-11.8 3.87-1.25 6.19-.75 9.5-.5 6.32 2.28 1.09 6.51-4.9"
        />
        <path
          className={classes.medium}
          d="M35 160.11l-8.65 31.06-5.75 4.74 1.14 3.22m12.84-39.61c.14.19.28.38.42.59 2-.74 4.34-1.44 6.86-2.11"
        />
        <path
          className={classes.line}
          d="M41.82 158a114.1 114.1 0 01-4.5 15.51M21.7 199.13c5.25-.1 10.47-7 15.62-25.62"
        />
      </g>
      <g
        id="fenderLeft"
        className={cn({
          [classes.selected]: damages[`fender${side}` as keyof FormDamage],
        })}
        onClick={() =>
          setDamage(
            `fender${side}`,
            !damages[`fender${side}` as keyof FormDamage]
          )
        }
      >
        <path
          className={classes.outline}
          d="M154.19 136.1a1 1 0 00-1.92-.4c-4.88.58-10 1.07-15.35 1.45a1.9 1.9 0 01-2-1.89 3.43 3.43 0 01.77-2.15c2.05-2.8 4.41-5.87 7-9.12a.5.5 0 00-.6-.76c-4.35-1.2-8.44-2.18-12.23-2.94a1 1 0 00-1.67-.52l-2.59 2.58c-.57 0-1.34-.11-1.72.44L114 137.53l-25.87 7.77c-4.82 1.77-9.45 3.69-14.56 6a1 1 0 00.42 1.91 5.11 5.11 0 001.33-.5h13.27c-6.17 8.93-12.88 15.26-23.67 18.81a.49.49 0 000 .6 115.13 115.13 0 0012.72 15c2.06 2.39 6.93 3.73 9.59 3.89l11.92.7a1 1 0 001.41.39 39.5 39.5 0 017.36-3.67 56.33 56.33 0 0122.55-2.66A1 1 0 00131 184c-.32-12.85 7.11-20.25 21.36-22.4a1 1 0 001.8-.6z"
        />
        <path
          className={classes.medium}
          d="M100 191.32a40.63 40.63 0 017.58-3.78 57.08 57.08 0 0122.95-2.71"
        />
        <path
          className={classes.line}
          d="M153.19 161l-2 .25c-12.19 2.39-21.19 8.05-20.65 23.59m22.65-48.74q-7.94 1-16.24 1.55a2.39 2.39 0 01-2.56-2.39 3.92 3.92 0 01.87-2.45c2.13-2.9 4.48-6 7-9.13m0 0c-5-1.39-9.42-2.44-13.4-3.2"
        />
        <path
          className={classes.fillDark}
          d="M154.19 136.1V161a1 1 0 01-2 0v-24.9a1 1 0 112 0z"
        />
        <path
          className={classes.medium}
          d="M88.47 146.24l26.15-7.85m0 0l10.06-15M74 152.26c4.35-2 9-4 14.48-6M126 123.35l2.89-2.87m-4.19 2.87h1.3"
        />
        <path
          className={classes.line}
          d="M74 152.26h15.55c-5.76 8.31-12.41 15.94-24.24 19.62m21.94 18.69l12.76.75m-22-4.5c1.83 2.15 6.46 3.58 9.28 3.75m-21.98-18.69A114.2 114.2 0 0078 186.82"
        />
      </g>
    </svg>
  )
}
